import { apiFetch } from './api';
import {
  invalidateCachedResourcesByPrefix,
  readCachedResource,
} from './requestCache';

export type ClientOrgRole = 'owner' | 'member';
export type ClientOrgPermissionKey =
  | 'inviteMembers'
  | 'createTickets'
  | 'manageBilling'
  | 'manageSubscription';

export type ClientOrgPermissions = Record<ClientOrgPermissionKey, boolean>;

export interface ClientOrgMember {
  uid: string;
  orgId: string | null;
  email: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  photoURL: string | null;
  role: ClientOrgRole;
  permissions: ClientOrgPermissions;
  isPrimaryAccountHolder: boolean;
}

export interface ClientOrgInvite {
  token: string;
  orgId: string | null;
  email: string | null;
  role: ClientOrgRole;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  createdAt: string | null;
  createdBy: string | null;
}

export interface ClientOrgBusinessProfile {
  orgId: string | null;
  orgName: string | null;
  organizationType: 'individual' | 'business';
  businessName: string | null;
  logoURL: string | null;
  logoStoragePath: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  address: string | null;
  isPrimaryAccountHolder: boolean;
  canEdit: boolean;
  isComplete: boolean;
  missingRequiredFields: string[];
}

export const CLIENT_ORG_ROLE_LABELS: Record<ClientOrgRole, string> = {
  owner: 'Primary account holder',
  member: 'Organization member',
};

type ClientOrgFetchOptions = {
  force?: boolean;
};

const CLIENT_ORG_CACHE_TTL_MS = 60_000;
const CLIENT_ORG_MEMBERS_CACHE_PREFIX = 'client-org:members:';
const CLIENT_ORG_PROFILE_CACHE_PREFIX = 'client-org:business-profile:';

const DEFAULT_PERMISSIONS_BY_ROLE: Record<ClientOrgRole, ClientOrgPermissions> = {
  owner: {
    inviteMembers: true,
    createTickets: true,
    manageBilling: true,
    manageSubscription: true,
  },
  member: {
    inviteMembers: false,
    createTickets: true,
    manageBilling: false,
    manageSubscription: false,
  },
};

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object');
}

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.message.trim().startsWith('404');
}

type ClientOrgApiMode = 'primary' | 'fallback';
let clientOrgApiMode: ClientOrgApiMode = 'primary';

function buildQuery(orgId?: string): string {
  const params = new URLSearchParams();
  if (orgId) params.set('orgId', orgId);
  params.set('scope', 'client_business');
  const q = params.toString();
  return q ? `?${q}` : '';
}

function buildMembersCacheKey(orgId?: string): string {
  const scope = orgId?.trim() || 'self';
  return `${CLIENT_ORG_MEMBERS_CACHE_PREFIX}${scope}`;
}

function buildBusinessProfileCacheKey(orgId?: string): string {
  const scope = orgId?.trim() || 'self';
  return `${CLIENT_ORG_PROFILE_CACHE_PREFIX}${scope}`;
}

export function invalidateClientOrgCache(): void {
  invalidateCachedResourcesByPrefix(CLIENT_ORG_MEMBERS_CACHE_PREFIX);
  invalidateCachedResourcesByPrefix(CLIENT_ORG_PROFILE_CACHE_PREFIX);
}

function toClientOrgRole(
  item: Record<string, unknown>,
): ClientOrgRole | null {
  const explicitRole = asText(item.orgRole)
    ?? asText(item.organizationRole)
    ?? asText(item.membershipRole)
    ?? asText(item.clientOrgRole)
    ?? asText(item.role);

  if (explicitRole) {
    const normalized = explicitRole.toLowerCase();
    if (normalized === 'owner') {
      return 'owner';
    }
    if (
      normalized === 'member' ||
      normalized === 'viewer' ||
      normalized === 'admin' ||
      normalized === 'client' ||
      normalized === 'editor'
    ) {
      return 'member';
    }
  }

  const fallbackRole = asText(item.role)?.toLowerCase();
  if (fallbackRole === 'client') return 'owner';
  return null;
}

function normalizePermissions(
  raw: unknown,
  role: ClientOrgRole,
): ClientOrgPermissions {
  const base = DEFAULT_PERMISSIONS_BY_ROLE[role];
  if (!isObject(raw)) return { ...base };

  return {
    inviteMembers:
      typeof raw.inviteMembers === 'boolean' ? raw.inviteMembers : base.inviteMembers,
    createTickets:
      typeof raw.createTickets === 'boolean' ? raw.createTickets : base.createTickets,
    manageBilling:
      typeof raw.manageBilling === 'boolean' ? raw.manageBilling : base.manageBilling,
    manageSubscription:
      typeof raw.manageSubscription === 'boolean'
        ? raw.manageSubscription
        : base.manageSubscription,
  };
}

function normalizeMember(item: unknown): ClientOrgMember | null {
  if (!isObject(item)) return null;

  const role = toClientOrgRole(item);
  if (!role) return null;

  const uid = asText(item.uid) ?? asText(item.id);
  if (!uid) return null;

  const email = asText(item.email);
  const displayName = asText(item.displayName) ?? asText(item.name);
  const firstName = asText(item.firstName);
  const lastName = asText(item.lastName);
  const orgId = asText(item.orgId) ?? asText(item.organizationId);
  const photoURL = asText(item.photoURL) ?? asText(item.avatarUrl);
  const isPrimaryAccountHolder =
    role === 'owner'
    || item.isPrimaryAccountHolder === true
    || item.primaryAccountHolder === true
    || item.isPrimary === true
    || item.primary === true;

  return {
    uid,
    orgId,
    email,
    displayName,
    firstName,
    lastName,
    photoURL,
    role,
    permissions: normalizePermissions(item.permissions, role),
    isPrimaryAccountHolder,
  };
}

function normalizeInvite(item: unknown): ClientOrgInvite | null {
  if (!isObject(item)) return null;

  const token = asText(item.token) ?? asText(item.id);
  const roleRaw = asText(item.role)?.toLowerCase();
  const statusRaw = asText(item.status)?.toLowerCase();
  if (!token || !roleRaw || !statusRaw) return null;

  if (roleRaw !== 'owner' && roleRaw !== 'admin' && roleRaw !== 'member' && roleRaw !== 'viewer') {
    return null;
  }
  if (statusRaw !== 'pending' && statusRaw !== 'accepted' && statusRaw !== 'revoked' && statusRaw !== 'expired') {
    return null;
  }

  return {
    token,
    orgId: asText(item.orgId) ?? asText(item.organizationId),
    email: asText(item.email),
    role: roleRaw === 'owner' ? 'owner' : 'member',
    status: statusRaw,
    createdAt: asText(item.createdAt),
    createdBy: asText(item.createdBy),
  };
}

function normalizeBusinessProfile(item: unknown): ClientOrgBusinessProfile {
  const record = isObject(item) ? item : {};
  const orgTypeRaw = asText(record.organizationType)?.toLowerCase();
  const organizationType = orgTypeRaw === 'business' ? 'business' : 'individual';
  const missingRaw = Array.isArray(record.missingRequiredFields)
    ? record.missingRequiredFields
    : [];
  const missingRequiredFields = missingRaw
    .map((value) => asText(value))
    .filter((value): value is string => Boolean(value));

  return {
    orgId: asText(record.orgId),
    orgName: asText(record.orgName),
    organizationType,
    businessName: asText(record.businessName),
    logoURL: asText(record.logoURL),
    logoStoragePath: asText(record.logoStoragePath),
    contactName: asText(record.contactName),
    contactEmail: asText(record.contactEmail),
    contactPhone: asText(record.contactPhone),
    website: asText(record.website),
    address: asText(record.address),
    isPrimaryAccountHolder: record.isPrimaryAccountHolder === true,
    canEdit: record.canEdit === true,
    isComplete: record.isComplete === true,
    missingRequiredFields,
  };
}

async function requestWithFallback<T>(
  primaryPath: string,
  fallbackPath: string,
  options?: RequestInit,
): Promise<T> {
  if (clientOrgApiMode === 'fallback') {
    try {
      return await apiFetch<T>(fallbackPath, options);
    } catch (error) {
      if (!isNotFoundError(error)) throw error;
      const response = await apiFetch<T>(primaryPath, options);
      clientOrgApiMode = 'primary';
      return response;
    }
  }

  try {
    return await apiFetch<T>(primaryPath, options);
  } catch (error) {
    if (!isNotFoundError(error)) throw error;
    const response = await apiFetch<T>(fallbackPath, options);
    clientOrgApiMode = 'fallback';
    return response;
  }
}

export async function fetchClientOrgMembers(
  orgId?: string,
  options: ClientOrgFetchOptions = {},
): Promise<ClientOrgMember[]> {
  const query = buildQuery(orgId);
  return readCachedResource(
    buildMembersCacheKey(orgId),
    async () => {
      const response = await requestWithFallback<any>(
        `/api/client/organization/members${query}`,
        `/api/team/members${query}`,
      );
      const raw = Array.isArray(response) ? response : response?.members;
      if (!Array.isArray(raw)) return [];
      return raw
        .map(normalizeMember)
        .filter((member): member is ClientOrgMember => Boolean(member));
    },
    {
      force: options.force,
      ttlMs: CLIENT_ORG_CACHE_TTL_MS,
    },
  );
}

export async function fetchClientOrgInvites(orgId?: string): Promise<ClientOrgInvite[]> {
  const query = buildQuery(orgId);
  const response = await requestWithFallback<any>(
    `/api/client/organization/invites${query}`,
    `/api/invites${query}`,
  );
  const raw = Array.isArray(response) ? response : response?.invites;
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeInvite).filter((invite): invite is ClientOrgInvite => Boolean(invite));
}

export async function createClientOrgInvite(input: {
  email: string;
  role: ClientOrgRole;
  orgId?: string;
}): Promise<ClientOrgInvite> {
  const payload = {
    email: input.email.trim().toLowerCase(),
    role: input.role,
    orgId: input.orgId,
    inviteType: 'client_external_access',
    scope: 'client_business',
  };
  const response = await requestWithFallback<any>(
    '/api/client/organization/invites',
    '/api/invites',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );

  const token =
    asText(response?.token)
    ?? `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const role: ClientOrgRole = input.role === 'owner' ? 'owner' : 'member';
  const statusRaw = asText(response?.status)?.toLowerCase();
  const status =
    statusRaw === 'pending' || statusRaw === 'accepted' || statusRaw === 'revoked' || statusRaw === 'expired'
      ? statusRaw
      : 'pending';

  return {
    token,
    orgId: asText(response?.orgId) ?? input.orgId ?? null,
    email: payload.email,
    role,
    status,
    createdAt: asText(response?.createdAt) ?? new Date().toISOString(),
    createdBy: asText(response?.createdBy) ?? null,
  };
}

export async function revokeClientOrgInvite(
  token: string,
  orgId?: string,
): Promise<void> {
  const query = buildQuery(orgId);
  await requestWithFallback(
    `/api/client/organization/invites/${encodeURIComponent(token)}${query}`,
    `/api/invites/${encodeURIComponent(token)}${query}`,
    {
      method: 'DELETE',
    },
  );
}

export async function updateClientOrgMember(
  uid: string,
  input: {
    role?: ClientOrgRole;
    permissions?: Partial<ClientOrgPermissions>;
    orgId?: string;
  },
): Promise<void> {
  const payload = {
    role: input.role,
    permissions: input.permissions,
    orgId: input.orgId,
    scope: 'client_business',
  };

  await requestWithFallback(
    `/api/client/organization/members/${encodeURIComponent(uid)}`,
    `/api/team/members/${encodeURIComponent(uid)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  invalidateClientOrgCache();
}

export async function removeClientOrgMember(uid: string, orgId?: string): Promise<void> {
  const query = buildQuery(orgId);
  await requestWithFallback(
    `/api/client/organization/members/${encodeURIComponent(uid)}${query}`,
    `/api/team/members/${encodeURIComponent(uid)}${query}`,
    {
      method: 'DELETE',
    },
  );
  invalidateClientOrgCache();
}

export async function transferClientOrgPrimaryAccountHolder(
  uid: string,
  input?: {
    orgId?: string;
  },
): Promise<{
  ok: boolean;
  orgId: string | null;
  previousOwnerUserId: string | null;
  nextOwnerUserId: string | null;
  migratedSubscriptionCount: number;
}> {
  const response = await apiFetch<any>(
    `/api/client/organization/primary-account-holder/${encodeURIComponent(uid)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        orgId: input?.orgId,
        scope: 'client_business',
      }),
    },
  );
  invalidateClientOrgCache();

  return {
    ok: response?.ok === true,
    orgId: asText(response?.orgId),
    previousOwnerUserId: asText(response?.previousOwnerUserId),
    nextOwnerUserId: asText(response?.nextOwnerUserId),
    migratedSubscriptionCount: Number(response?.migratedSubscriptionCount ?? 0) || 0,
  };
}

export async function fetchClientOrgBusinessProfile(
  orgId?: string,
  options: ClientOrgFetchOptions = {},
): Promise<ClientOrgBusinessProfile> {
  const query = buildQuery(orgId);
  return readCachedResource(
    buildBusinessProfileCacheKey(orgId),
    async () => {
      const response = await requestWithFallback<any>(
        `/api/client/organization/business-profile${query}`,
        `/api/team/business-profile${query}`,
      );
      return normalizeBusinessProfile(response);
    },
    {
      force: options.force,
      ttlMs: CLIENT_ORG_CACHE_TTL_MS,
    },
  );
}

export async function updateClientOrgBusinessProfile(input: {
  orgId?: string;
  organizationType?: 'individual' | 'business';
  businessName?: string;
  logoURL?: string;
  logoStoragePath?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  address?: string;
}): Promise<ClientOrgBusinessProfile> {
  const payload = {
    orgId: input.orgId,
    organizationType: input.organizationType,
    businessName: input.businessName,
    logoURL: input.logoURL,
    logoStoragePath: input.logoStoragePath,
    contactName: input.contactName,
    contactEmail: input.contactEmail,
    contactPhone: input.contactPhone,
    website: input.website,
    address: input.address,
  };

  const response = await requestWithFallback<any>(
    '/api/client/organization/business-profile',
    '/api/team/business-profile',
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  invalidateClientOrgCache();
  return normalizeBusinessProfile(response);
}

export async function deleteClientOrganization(orgId?: string): Promise<void> {
  const query = buildQuery(orgId);
  await requestWithFallback(
    `/api/client/organization${query}`,
    `/api/team/organization${query}`,
    {
      method: 'DELETE',
    },
  );
  invalidateClientOrgCache();
}

export async function leaveClientOrganization(orgId?: string): Promise<{
  ok: boolean;
  left: boolean;
  orgId: string | null;
  nextActiveOrgId: string | null;
  remainingMembershipCount: number;
}> {
  const query = buildQuery(orgId);
  const response = await requestWithFallback<any>(
    `/api/client/organization/membership${query}`,
    `/api/team/membership${query}`,
    {
      method: 'DELETE',
    },
  );
  invalidateClientOrgCache();

  return {
    ok: response?.ok === true,
    left: response?.left === true,
    orgId: asText(response?.orgId),
    nextActiveOrgId: asText(response?.nextActiveOrgId),
    remainingMembershipCount: Number(response?.remainingMembershipCount ?? 0) || 0,
  };
}
